import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr, fmtDate } from "../lib/api";
import TopBar from "../components/TopBar";
import { Card, Chip, Empty, Skeleton, Button, Select, Input, Sheet } from "../components/ui";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, Phone, Trophy, Shield, Save, X, Trash2, Pencil, Mail } from "lucide-react";
import { Avatar } from "./MembersPage";
import ResetLinkPanel from "../components/ResetLinkPanel";
import { useAuth } from "../context/AuthContext";

const SKILL_LEVELS = [
  { value: "good", label: "Good" },
  { value: "average", label: "Average" },
  { value: "ok", label: "OK" },
];

const UMPIRE_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export default function MemberDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { club } = useAuth();
  const isOwner = club?.my_role === "owner";
  const isAdmin = club?.my_role === "admin";
  const canEditSkills = isOwner || isAdmin;
  const canEditDetails = ["owner", "admin", "manager"].includes(club?.my_role);
  const [editOpen, setEditOpen] = useState(false);
  const [member, setMember] = useState(null);
  const [txns, setTxns] = useState(null);
  const [editingSkills, setEditingSkills] = useState(false);
  const [skillForm, setSkillForm] = useState({});
  const [savingSkills, setSavingSkills] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: m }, { data: t }] = await Promise.all([
          api.get(`/members/${id}`),
          api.get(`/transactions?member_id=${id}`),
        ]);
        setMember(m);
        setTxns(t);
        setSkillForm({
          batting_skill: m.batting_skill || "ok",
          bowling_skill: m.bowling_skill || "ok",
          fielding_skill: m.fielding_skill || "ok",
          keeping_skill: m.keeping_skill || "ok",
          can_umpire: m.can_umpire ? "true" : "false",
        });
      } catch (err) { toast.error(apiError(err)); nav("/members"); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const saveSkills = async () => {
    setSavingSkills(true);
    try {
      await api.patch(`/members/${id}/skills`, {
        batting_skill: skillForm.batting_skill,
        bowling_skill: skillForm.bowling_skill,
        fielding_skill: skillForm.fielding_skill,
        keeping_skill: skillForm.keeping_skill,
        can_umpire: skillForm.can_umpire === "true",
      });
      setMember({
        ...member,
        batting_skill: skillForm.batting_skill,
        bowling_skill: skillForm.bowling_skill,
        fielding_skill: skillForm.fielding_skill,
        keeping_skill: skillForm.keeping_skill,
        can_umpire: skillForm.can_umpire === "true",
      });
      setEditingSkills(false);
      toast.success("Skills updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingSkills(false); }
  };

  const cancelEditSkills = () => {
    setEditingSkills(false);
    setSkillForm({
      batting_skill: member.batting_skill || "ok",
      bowling_skill: member.bowling_skill || "ok",
      fielding_skill: member.fielding_skill || "ok",
      keeping_skill: member.keeping_skill || "ok",
      can_umpire: member.can_umpire ? "true" : "false",
    });
  };

  const toggleAdmin = async () => {
    const newRole = member.club_role === "admin" ? "member" : "admin";
    try {
      await api.patch(`/members/${id}/role`, { role: newRole });
      setMember({ ...member, club_role: newRole });
      toast.success(newRole === "admin" ? "Promoted to admin" : "Removed admin rights");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleDeleteMember = async () => {
    if (!window.confirm(`Remove ${member.name} from this club? They will no longer be a member.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/members/${id}`);
      toast.success("Member removed");
      nav("/members", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const saveDetails = async (body) => {
    try {
      const { data } = await api.patch(`/members/${id}`, body);
      setMember((m) => ({ ...m, ...data }));
      setEditOpen(false);
      toast.success("Player details updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!member) {
    return (
      <div className="pb-24 px-5 pt-6">
        <Skeleton className="h-8 w-40 mb-4" />
        <Skeleton className="h-40 mb-3" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="pb-28">
      <div className="flex items-center px-3 pt-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
      </div>

      <div className="px-5 pt-2">
        <div className="flex items-center gap-4">
          <Avatar member={member} size={72} />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">{member.name}</h1>
            <div className="text-sm text-ink-muted capitalize">{member.role}</div>
            {member.club_role && (
              <div className="mt-1">
                <Chip tone={member.club_role === "admin" ? "success" : "default"}>
                  <Shield size={10} className="inline mr-1" />
                  {member.club_role}
                </Chip>
              </div>
            )}
            {member.mobile && (
              <a href={`tel:${member.mobile}`} className="text-xs text-brand font-semibold flex items-center gap-1 mt-1" data-testid="member-call">
                <Phone size={12} /> {member.mobile}
              </a>
            )}
            {member.email && (
              <div className="text-xs text-ink-muted flex items-center gap-1 mt-1 truncate" data-testid="member-email">
                <Mail size={12} /> {member.email}
              </div>
            )}
          </div>
          {canEditDetails && (
            <button
              onClick={() => setEditOpen(true)}
              className="ml-auto h-10 px-3 rounded-2xl bg-black/[0.04] hover:bg-black/[0.07] text-sm font-semibold flex items-center gap-1.5 shrink-0"
              data-testid="member-edit-btn"
            >
              <Pencil size={14} /> Edit
            </button>
          )}
        </div>

        {canEditDetails && member.has_account && !member.is_me && (
          <Card className="mt-4 p-4">
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1">Password reset</div>
            <p className="text-xs text-ink-muted mb-3">
              Works after {member.name.split(" ")[0]} taps "Forgot password?" on the login page.
            </p>
            <ResetLinkPanel memberId={member.id} name={member.name} />
          </Card>
        )}

        {isOwner && member.club_role !== "owner" && (
          <div className="mt-4">
            <Button
              variant={member.club_role === "admin" ? "outline" : "primary"}
              size="lg"
              className="w-full"
              onClick={toggleAdmin}
              data-testid="member-toggle-admin"
            >
              <Shield size={16} />
              {member.club_role === "admin" ? "Remove admin" : "Make admin"}
            </Button>
          </div>
        )}

        {canEditSkills && member.club_role !== "owner" && (
          <div className="mt-3">
            <Button
              variant="outline"
              size="lg"
              className="w-full text-red-500 border-red-200 hover:bg-red-50"
              onClick={handleDeleteMember}
              disabled={deleting}
              data-testid="member-delete-btn"
            >
              <Trash2 size={16} />
              {deleting ? "Removing…" : "Remove from club"}
            </Button>
          </div>
        )}

        {canEditSkills && !editingSkills && (
          <div className="mt-4">
            <Card>
              <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">
                Cricket Skills
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <SkillDisplay label="Batting" value={member.batting_skill} />
                <SkillDisplay label="Bowling" value={member.bowling_skill} />
                <SkillDisplay label="Fielding" value={member.fielding_skill} />
                <SkillDisplay label="Keeping" value={member.keeping_skill} />
              </div>
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-black/[0.06]">
                <div className="text-sm text-ink-muted">Can Umpire</div>
                <Chip tone={member.can_umpire ? "success" : "default"}>{member.can_umpire ? "Yes" : "No"}</Chip>
              </div>
              <Button variant="outline" size="md" className="w-full" onClick={() => setEditingSkills(true)}>
                Edit Skills
              </Button>
            </Card>
          </div>
        )}

        {canEditSkills && editingSkills && (
          <div className="mt-4">
            <Card>
              <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">
                Edit Cricket Skills
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Select
                  label="Batting"
                  value={skillForm.batting_skill}
                  onChange={(e) => setSkillForm({ ...skillForm, batting_skill: e.target.value })}
                >
                  {SKILL_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Bowling"
                  value={skillForm.bowling_skill}
                  onChange={(e) => setSkillForm({ ...skillForm, bowling_skill: e.target.value })}
                >
                  {SKILL_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Fielding"
                  value={skillForm.fielding_skill}
                  onChange={(e) => setSkillForm({ ...skillForm, fielding_skill: e.target.value })}
                >
                  {SKILL_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Keeping"
                  value={skillForm.keeping_skill}
                  onChange={(e) => setSkillForm({ ...skillForm, keeping_skill: e.target.value })}
                >
                  {SKILL_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="mb-4">
                <Select
                  label="Can Umpire?"
                  value={skillForm.can_umpire}
                  onChange={(e) => setSkillForm({ ...skillForm, can_umpire: e.target.value })}
                >
                  {UMPIRE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="md"
                  className="w-full text-red-500 border-red-200"
                  onClick={cancelEditSkills}
                  disabled={savingSkills}
                >
                  <X size={16} /> Cancel
                </Button>
                <Button
                  size="md"
                  className="w-full"
                  onClick={saveSkills}
                  disabled={savingSkills}
                >
                  <Save size={16} /> {savingSkills ? "Saving…" : "Save"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2.5 mt-5">
          <Stat label="Balance" value={inr(member.balance)} tone={member.balance < 0 ? "danger" : "success"} />
          <Stat label="Matches" value={member.matches_played || 0} />
          <Stat
            label="Style"
            value={member.batting_style || member.bowling_style || "—"}
          />
        </div>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">
            Transactions
          </div>
          {txns === null ? (
            <Skeleton className="h-40" />
          ) : txns.length === 0 ? (
            <Card className="p-0">
              <Empty icon={Trophy} title="No activity yet" subtitle="This member hasn't been in any expenses." />
            </Card>
          ) : (
            <Card className="p-2 divide-y divide-black/[0.06]">
              {txns.map((t) => {
                const positive = t.amount >= 0;
                const Icon = positive ? ArrowUpRight : ArrowDownRight;
                return (
                  <div key={t.id} className="flex items-center gap-3 px-2 py-3">
                    <div className={`h-9 w-9 rounded-2xl grid place-items-center ${positive ? "bg-brand-50 text-brand" : "bg-red-50 text-red-500"}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{t.description || t.category}</div>
                      <div className="text-xs text-ink-muted">{fmtDate(t.created_at)}</div>
                    </div>
                    <div className={`text-sm font-bold ${positive ? "text-brand" : "text-red-500"}`}>
                      {inr(t.amount)}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}
        </div>
      </div>
      <EditDetailsSheet open={editOpen} onClose={() => setEditOpen(false)} member={member} onSave={saveDetails} />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = { danger: "text-red-500", success: "text-brand" };
  return (
    <div className="rounded-2xl bg-white border border-black/[0.06] shadow-card p-3">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold">{label}</div>
      <div className={`text-base font-bold mt-0.5 truncate ${tones[tone] || "text-ink"}`}>{value}</div>
    </div>
  );
}

function SkillDisplay({ label, value }) {
  const skillColors = {
    good: "bg-green-100 text-green-700",
    average: "bg-blue-100 text-blue-700",
    ok: "bg-gray-100 text-gray-700",
  };
  return (
    <div>
      <div className="text-xs text-ink-muted mb-1">{label}</div>
      <div className={`px-2 py-1 rounded text-xs font-semibold capitalize ${skillColors[value] || skillColors.ok}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function EditDetailsSheet({ open, onClose, member, onSave }) {
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: member.name || "", mobile: member.mobile || "", email: member.email || "" });
  }, [open, member]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { name: form.name, mobile: form.mobile };
    if (!member.has_account) body.email = form.email;
    await onSave(body);
    setSaving(false);
  };

  return (
    <Sheet open={open} onClose={onClose} title="Edit player details">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Name *" value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="edit-member-name" />
        <Input label="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91…" data-testid="edit-member-mobile" />
        <div>
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={member.has_account}
            placeholder="player@example.com"
            className={member.has_account ? "bg-black/[0.03] text-ink-muted" : ""}
            data-testid="edit-member-email"
          />
          {member.has_account && (
            <p className="text-xs text-ink-muted mt-1.5">
              This is {member.name.split(" ")[0]}'s login email, so only they can change it (Profile → Edit).
            </p>
          )}
        </div>
        <Button type="submit" size="lg" className="w-full" disabled={saving || !form.name.trim()} data-testid="edit-member-save">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </Button>
      </form>
    </Sheet>
  );
}
